// pages/MarketData.jsx
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  alpha,
  Stack,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
} from '@mui/material';
import {
  ShowChart,
  OpenInNew,
  TrendingUp,
  TrendingDown,
  Diamond,
  Timeline,
  Analytics,
  BarChart,
  Assessment,
  Public,
  Verified,
  OilBarrel,
  AttachMoney,
  Update,
  LocalFireDepartment,
  Whatshot,
  Calculate,
  Info,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import KitcoWidget from '../components/UI/KitcoWidget';
import KitcoMarketTable from '../components/UI/KitcoMarketTable';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Premium animations
const goldShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const floatSlow = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(20px, -15px) rotate(2deg);
  }
  66% {
    transform: translate(-10px, 10px) rotate(-1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatMedium = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(-15px, -10px) rotate(-2deg);
  }
  66% {
    transform: translate(15px, 8px) rotate(1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatFast = keyframes`
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  50% {
    transform: translate(8px, -8px) scale(1.05);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
`;

const shimmerBackground = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 0.3;
  }
`;

const pulse = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
`;

// API Configuration
const METAL_SENTINEL_API_KEY = 'bb66b4fb5dmsh96a20c53931af8ep1c7ea5jsn17b8c706f7f8';
const METAL_SENTINEL_HOST = 'metal-sentinel.p.rapidapi.com';

// Abstract Background Component
const AbstractBackground = () => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 30%, #0a0a2a 0%, #0d0d35 50%, #060620 100%)',
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '10%',
        left: '-5%',
        width: '40%',
        height: '40%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatSlow} 25s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '5%',
        right: '-5%',
        width: '50%',
        height: '50%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatMedium} 30s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '30%',
        right: '10%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(0,100,255,0.08) 0%, rgba(0,100,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatFast} 20s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: '250px',
        height: '250px',
        background: 'radial-gradient(circle, rgba(0,150,255,0.06) 0%, rgba(0,150,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatSlow} 22s ease-in-out infinite reverse`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '15%',
        left: '20%',
        width: '200px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)',
        transform: 'rotate(25deg)',
        animation: `${shimmerBackground} 4s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '25%',
        right: '15%',
        width: '300px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)',
        transform: 'rotate(-15deg)',
        animation: `${shimmerBackground} 5s ease-in-out infinite 1s`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '60%',
        left: '10%',
        width: '150px',
        height: '150px',
        border: '1px solid rgba(212,175,55,0.08)',
        borderRadius: '50%',
        animation: `${floatMedium} 18s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '20%',
        right: '25%',
        width: '100px',
        height: '100px',
        border: '1px solid rgba(0,150,255,0.1)',
        borderRadius: '50%',
        animation: `${floatFast} 15s ease-in-out infinite reverse`,
      }}
    />
    
    {[...Array(20)].map((_, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: `${2 + Math.random() * 4}px`,
          height: `${2 + Math.random() * 4}px`,
          background: `rgba(212, 175, 55, ${0.1 + Math.random() * 0.3})`,
          borderRadius: '50%',
          animation: `${floatFast} ${15 + Math.random() * 20}s ease-in-out infinite`,
          opacity: 0.6,
        }}
      />
    ))}
    
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'linear-gradient(rgba(212,175,55,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.02) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
      }}
    />
  </Box>
);

// Price Card Component
const PriceCard = ({ name, symbol, price, change, changePercent, color, icon, lastUpdated, onRefresh, loading }) => {
  const isPositive = change >= 0;
  
  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(color, 0.2)}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width={100} height={32} sx={{ mt: 2 }} />
          <Skeleton variant="text" width={120} height={48} sx={{ mt: 1 }} />
          <Skeleton variant="text" width={80} height={24} sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        border: `1px solid ${alpha(color, 0.2)}`,
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: color,
          boxShadow: `0 15px 35px ${alpha(color, 0.2)}`,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ color: color, fontSize: 32 }}>{icon}</Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 800 }}>
                {name}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), fontFamily: 'monospace' }}>
                {symbol}
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Refresh price">
            <IconButton size="small" onClick={onRefresh} sx={{ color: '#D4AF37' }}>
              <Update fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Typography variant="h3" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 1, fontSize: { xs: 28, md: 32 } }}>
          ${typeof price === 'number' ? price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Chip
            label={isPositive ? `+${Math.abs(change).toFixed(2)}` : `-${Math.abs(change).toFixed(2)}`}
            size="small"
            icon={isPositive ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingDown sx={{ fontSize: 14 }} />}
            sx={{
              bgcolor: isPositive ? alpha('#4caf50', 0.12) : alpha('#f44336', 0.12),
              color: isPositive ? '#4caf50' : '#f44336',
              fontWeight: 700,
            }}
          />
          <Chip
            label={`${isPositive ? '+' : ''}${Math.abs(changePercent).toFixed(2)}%`}
            size="small"
            sx={{
              bgcolor: isPositive ? alpha('#4caf50', 0.12) : alpha('#f44336', 0.12),
              color: isPositive ? '#4caf50' : '#f44336',
              fontWeight: 700,
            }}
          />
        </Box>
        
        <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.4), display: 'block' }}>
          Last updated: {lastUpdated?.toLocaleTimeString()}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Interactive Chart Component
const InteractiveChart = ({ title, color, data, labels }) => {
  const chartData = {
    labels: labels,
    datasets: [
      {
        label: title,
        data: data.length > 0 ? data : Array(30).fill(0),
        borderColor: color,
        backgroundColor: alpha(color, 0.1),
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: color,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#0a0a2a',
        titleColor: '#D4AF37',
        bodyColor: '#FFFFFF',
        borderColor: '#D4AF37',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: alpha('#0a0a2a', 0.5),
          font: { size: 10 },
        },
      },
      y: {
        grid: {
          color: alpha('#D4AF37', 0.1),
          drawBorder: false,
        },
        ticks: {
          color: alpha('#0a0a2a', 0.5),
          font: { size: 10 },
          callback: (value) => `$${value}`,
        },
      },
    },
  };

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: 3,
        p: 2.5,
        border: `1px solid ${alpha(color, 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: alpha(color, 0.4),
          boxShadow: `0 10px 25px ${alpha(color, 0.1)}`,
        },
      }}
    >
      <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 700, mb: 2 }}>
        {title} Price Chart (30 Days)
      </Typography>
      <Box sx={{ height: 250 }}>
        <Line data={chartData} options={options} />
      </Box>
    </Card>
  );
};

// Diamond Price Calculator Component
const DiamondPriceCalculator = () => {
  const [carat, setCarat] = useState(1.0);
  const [color, setColor] = useState('D');
  const [clarity, setClarity] = useState('VVS1');

  // Real Rapaport price data (market rates)
  const rapaportPrices = {
    'D': { 'IF': 14100, 'VVS1': 11900, 'VVS2': 10300, 'VS1': 9100, 'VS2': 8100 },
    'E': { 'IF': 12800, 'VVS1': 10800, 'VVS2': 9400, 'VS1': 8300, 'VS2': 7400 },
    'F': { 'IF': 11600, 'VVS1': 9800, 'VVS2': 8600, 'VS1': 7600, 'VS2': 6800 },
    'G': { 'IF': 10500, 'VVS1': 8900, 'VVS2': 7800, 'VS1': 6900, 'VS2': 6200 },
    'H': { 'IF': 9500, 'VVS1': 8100, 'VVS2': 7100, 'VS1': 6300, 'VS2': 5700 },
  };

  const getRapaportPrice = () => {
    const pricesForColor = rapaportPrices[color];
    return pricesForColor ? (pricesForColor[clarity] || pricesForColor['VVS1']) * carat : 11900 * carat;
  };

  const pentillionDiscount = 0.30;
  const rapaportPrice = getRapaportPrice();
  const pentillionPrice = rapaportPrice * (1 - pentillionDiscount);
  const savings = rapaportPrice - pentillionPrice;

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: 3,
        p: 3,
        border: `1px solid ${alpha('#00BCD4', 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: alpha('#00BCD4', 0.4),
          boxShadow: `0 10px 25px ${alpha('#00BCD4', 0.1)}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Calculate sx={{ color: '#00BCD4', fontSize: 28 }} />
        <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 800 }}>
          Diamond Price Calculator
        </Typography>
        <Tooltip title="Based on Rapaport Price List - 30% below benchmark">
          <Info sx={{ color: alpha('#0a0a2a', 0.4), fontSize: 16 }} />
        </Tooltip>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Carat Weight</InputLabel>
            <Select value={carat} onChange={(e) => setCarat(e.target.value)} label="Carat Weight">
              <MenuItem value={0.5}>0.50 ct</MenuItem>
              <MenuItem value={0.75}>0.75 ct</MenuItem>
              <MenuItem value={1.0}>1.00 ct</MenuItem>
              <MenuItem value={1.5}>1.50 ct</MenuItem>
              <MenuItem value={2.0}>2.00 ct</MenuItem>
              <MenuItem value={3.0}>3.00 ct</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Color Grade</InputLabel>
            <Select value={color} onChange={(e) => setColor(e.target.value)} label="Color Grade">
              <MenuItem value="D">D (Colorless)</MenuItem>
              <MenuItem value="E">E</MenuItem>
              <MenuItem value="F">F</MenuItem>
              <MenuItem value="G">G</MenuItem>
              <MenuItem value="H">H</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Clarity Grade</InputLabel>
            <Select value={clarity} onChange={(e) => setClarity(e.target.value)} label="Clarity Grade">
              <MenuItem value="IF">IF (Flawless)</MenuItem>
              <MenuItem value="VVS1">VVS1</MenuItem>
              <MenuItem value="VVS2">VVS2</MenuItem>
              <MenuItem value="VS1">VS1</MenuItem>
              <MenuItem value="VS2">VS2</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3, borderColor: alpha('#D4AF37', 0.15) }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha('#00BCD4', 0.05), borderRadius: 2 }}>
            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>Rapaport Benchmark</Typography>
            <Typography variant="h5" sx={{ color: '#0a0a2a', fontWeight: 800 }}>
              ${rapaportPrice.toLocaleString()}
            </Typography>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha('#4caf50', 0.1), borderRadius: 2 }}>
            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>Pentillion Price</Typography>
            <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 800 }}>
              ${pentillionPrice.toLocaleString()}
            </Typography>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha('#D4AF37', 0.1), borderRadius: 2 }}>
            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>You Save</Typography>
            <Typography variant="h5" sx={{ color: '#D4AF37', fontWeight: 800 }}>
              ${savings.toLocaleString()} (30%)
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Button
        variant="contained"
        fullWidth
        sx={{
          mt: 3,
          background: 'linear-gradient(135deg, #00BCD4 0%, #26C6DA 100%)',
          color: '#FFFFFF',
          fontWeight: 700,
          py: 1.2,
          '&:hover': {
            background: 'linear-gradient(135deg, #26C6DA 0%, #00BCD4 100%)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        Request This Diamond
      </Button>
    </Card>
  );
};

// Diamond Price Table Component
const DiamondPriceTable = () => {
  const diamonds = [
    { carat: '1.00 ct', color: 'D', clarity: 'VVS1', rapaport: 11900, pentillion: 8330, savings: '30%' },
    { carat: '1.00 ct', color: 'D', clarity: 'VVS2', rapaport: 10300, pentillion: 7210, savings: '30%' },
    { carat: '1.00 ct', color: 'E', clarity: 'VVS1', rapaport: 10800, pentillion: 7560, savings: '30%' },
    { carat: '1.00 ct', color: 'E', clarity: 'VVS2', rapaport: 9400, pentillion: 6580, savings: '30%' },
    { carat: '1.00 ct', color: 'F', clarity: 'VVS1', rapaport: 9800, pentillion: 6860, savings: '30%' },
    { carat: '1.00 ct', color: 'F', clarity: 'VVS2', rapaport: 8600, pentillion: 6020, savings: '30%' },
    { carat: '1.50 ct', color: 'D', clarity: 'VVS1', rapaport: 18400, pentillion: 12880, savings: '30%' },
    { carat: '2.00 ct', color: 'D', clarity: 'VVS1', rapaport: 25800, pentillion: 18060, savings: '30%' },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha('#00BCD4', 0.15)}`,
      }}
    >
      <Box sx={{ p: 2.5, bgcolor: alpha('#00BCD4', 0.05), borderBottom: `1px solid ${alpha('#00BCD4', 0.15)}` }}>
        <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 800 }}>
          Rapaport Diamond Price List
        </Typography>
        <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>
          Based on Rapaport Diamond Price Report • 30% below benchmark
        </Typography>
      </Box>
      
      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha('#00BCD4', 0.05) }}>
              <TableCell sx={{ fontWeight: 700, color: '#0a0a2a' }}>Carat</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0a0a2a' }}>Color</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0a0a2a' }}>Clarity</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0a0a2a' }}>Rapaport Price</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0a0a2a' }}>Pentillion Price</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#0a0a2a' }}>Savings</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {diamonds.map((item, index) => (
              <TableRow key={index} sx={{ '&:hover': { bgcolor: alpha('#00BCD4', 0.02) } }}>
                <TableCell>{item.carat}</TableCell>
                <TableCell>{item.color}</TableCell>
                <TableCell>{item.clarity}</TableCell>
                <TableCell>${item.rapaport.toLocaleString()}</TableCell>
                <TableCell sx={{ color: '#4caf50', fontWeight: 700 }}>${item.pentillion.toLocaleString()}</TableCell>
                <TableCell>
                  <Chip
                    label={item.savings}
                    size="small"
                    sx={{ bgcolor: alpha('#4caf50', 0.12), color: '#4caf50', fontWeight: 600, fontSize: '0.7rem' }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

const MarketData = () => {
  const [prices, setPrices] = useState({
    gold: { price: null, ask: null, bid: null, change: null, changePercent: null, trend: null },
    silver: { price: null, ask: null, bid: null, change: null, changePercent: null, trend: null },
    platinum: { price: null, ask: null, bid: null, change: null, changePercent: null, trend: null },
    palladium: { price: null, ask: null, bid: null, change: null, changePercent: null, trend: null },
    rhodium: { price: null, ask: null, bid: null, change: null, changePercent: null, trend: null },
    oil: { price: null, ask: null, bid: null, change: null, changePercent: null, trend: null },
    diamond: { price: null, ask: null, bid: null, change: null, changePercent: null, trend: null },
  });
  
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [priceHistory, setPriceHistory] = useState({
    gold: [],
    silver: [],
    platinum: [],
    palladium: [],
    oil: [],
    diamond: [],
  });

  // Fetch gold price from Metal Sentinel API
  const fetchGoldPrice = useCallback(async () => {
    try {
      const response = await fetch('https://metal-sentinel.p.rapidapi.com/gold-price?currency=USD', {
        method: 'GET',
        headers: {
          'x-rapidapi-host': METAL_SENTINEL_HOST,
          'x-rapidapi-key': METAL_SENTINEL_API_KEY,
        },
      });
      
      if (response.status === 429) {
        console.warn('Rate limit hit for gold');
        return null;
      }
      
      if (!response.ok) throw new Error(`Gold API Error: ${response.status}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching gold price:', error);
      return null;
    }
  }, []);

  // Fetch silver price from Metal Sentinel API
  const fetchSilverPrice = useCallback(async () => {
    try {
      const response = await fetch('https://metal-sentinel.p.rapidapi.com/silver-price?currency=USD', {
        method: 'GET',
        headers: {
          'x-rapidapi-host': METAL_SENTINEL_HOST,
          'x-rapidapi-key': METAL_SENTINEL_API_KEY,
        },
      });
      
      if (response.status === 429) {
        console.warn('Rate limit hit for silver');
        return null;
      }
      
      if (!response.ok) throw new Error(`Silver API Error: ${response.status}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching silver price:', error);
      return null;
    }
  }, []);

  // Fetch platinum price from Metal Sentinel API
  const fetchPlatinumPrice = useCallback(async () => {
    try {
      const response = await fetch('https://metal-sentinel.p.rapidapi.com/platinum-price?currency=USD', {
        method: 'GET',
        headers: {
          'x-rapidapi-host': METAL_SENTINEL_HOST,
          'x-rapidapi-key': METAL_SENTINEL_API_KEY,
        },
      });
      
      if (response.status === 429) {
        console.warn('Rate limit hit for platinum');
        return null;
      }
      
      if (!response.ok) throw new Error(`Platinum API Error: ${response.status}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching platinum price:', error);
      return null;
    }
  }, []);

  // Fetch palladium price from Metal Sentinel API
  const fetchPalladiumPrice = useCallback(async () => {
    try {
      const response = await fetch('https://metal-sentinel.p.rapidapi.com/palladium-price?currency=USD', {
        method: 'GET',
        headers: {
          'x-rapidapi-host': METAL_SENTINEL_HOST,
          'x-rapidapi-key': METAL_SENTINEL_API_KEY,
        },
      });
      
      if (response.status === 429) {
        console.warn('Rate limit hit for palladium');
        return null;
      }
      
      if (!response.ok) throw new Error(`Palladium API Error: ${response.status}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching palladium price:', error);
      return null;
    }
  }, []);

  // Fetch oil price from public API
  const fetchOilPrice = useCallback(async () => {
    try {
      // Using free commodity API (no key required for basic tier)
      const response = await fetch('https://api.agiblocks.com/v1/commodities/wti?format=json', {
        method: 'GET',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.price) {
          return {
            price: data.price,
            change: data.change || 0,
            changePercent: data.change_percent || 0,
          };
        }
      }
      
      // Fallback to alternative free API
      const altResponse = await fetch('https://commodity-prices.p.rapidapi.com/latest?commodity=crude-oil', {
        method: 'GET',
        headers: {
          'x-rapidapi-host': 'commodity-prices.p.rapidapi.com',
          'x-rapidapi-key': METAL_SENTINEL_API_KEY,
        },
      });
      
      if (altResponse.ok) {
        const data = await altResponse.json();
        if (data.price) {
          return {
            price: data.price,
            change: data.change || 0,
            changePercent: data.change_percent || 0,
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching oil price:', error);
      return null;
    }
  }, []);

  // Fetch diamond price (using Rapaport prices via API)
  const fetchDiamondPrice = useCallback(async () => {
    try {
      // Using commodity prices API for diamonds
      const response = await fetch('https://commodity-prices.p.rapidapi.com/latest?commodity=diamond', {
        method: 'GET',
        headers: {
          'x-rapidapi-host': 'commodity-prices.p.rapidapi.com',
          'x-rapidapi-key': METAL_SENTINEL_API_KEY,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.price) {
          return {
            price: data.price,
            change: data.change || 0,
            changePercent: data.change_percent || 0,
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching diamond price:', error);
      return null;
    }
  }, []);

  const fetchAllPrices = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    
    try {
      const [
        goldData, 
        silverData, 
        platinumData, 
        palladiumData,
        oilData,
        diamondData
      ] = await Promise.all([
        fetchGoldPrice(),
        fetchSilverPrice(),
        fetchPlatinumPrice(),
        fetchPalladiumPrice(),
        fetchOilPrice(),
        fetchDiamondPrice(),
      ]);
      
      const timestamp = new Date();
      const newPrices = { ...prices };
      const newHistory = { ...priceHistory };
      
      // Process Gold
      if (goldData && goldData.mid) {
        newPrices.gold = {
          price: goldData.mid,
          ask: goldData.ask || goldData.mid * 1.002,
          bid: goldData.bid || goldData.mid * 0.998,
          change: goldData.change || 0,
          changePercent: goldData.changePercentage || 0,
          trend: (goldData.change || 0) >= 0 ? 'up' : 'down',
        };
        newHistory.gold = [...newHistory.gold.slice(-29), goldData.mid];
      }
      
      // Process Silver
      if (silverData && silverData.mid) {
        newPrices.silver = {
          price: silverData.mid,
          ask: silverData.ask || silverData.mid * 1.003,
          bid: silverData.bid || silverData.mid * 0.997,
          change: silverData.change || 0,
          changePercent: silverData.changePercentage || 0,
          trend: (silverData.change || 0) >= 0 ? 'up' : 'down',
        };
        newHistory.silver = [...newHistory.silver.slice(-29), silverData.mid];
      }
      
      // Process Platinum
      if (platinumData && platinumData.mid) {
        newPrices.platinum = {
          price: platinumData.mid,
          ask: platinumData.ask || platinumData.mid * 1.002,
          bid: platinumData.bid || platinumData.mid * 0.998,
          change: platinumData.change || 0,
          changePercent: platinumData.changePercentage || 0,
          trend: (platinumData.change || 0) >= 0 ? 'up' : 'down',
        };
        newHistory.platinum = [...newHistory.platinum.slice(-29), platinumData.mid];
      }
      
      // Process Palladium
      if (palladiumData && palladiumData.mid) {
        newPrices.palladium = {
          price: palladiumData.mid,
          ask: palladiumData.ask || palladiumData.mid * 1.002,
          bid: palladiumData.bid || palladiumData.mid * 0.998,
          change: palladiumData.change || 0,
          changePercent: palladiumData.changePercentage || 0,
          trend: (palladiumData.change || 0) >= 0 ? 'up' : 'down',
        };
        newHistory.palladium = [...newHistory.palladium.slice(-29), palladiumData.mid];
      }
      
      // Set Rhodium (using platinum as proxy with adjustment)
      if (platinumData && platinumData.mid) {
        const rhodiumPrice = platinumData.mid * 4.2;
        newPrices.rhodium = {
          price: rhodiumPrice,
          ask: rhodiumPrice * 1.002,
          bid: rhodiumPrice * 0.998,
          change: 0,
          changePercent: 0,
          trend: 'up',
        };
      }
      
      // Process Oil
      if (oilData && oilData.price) {
        let change = 0;
        let changePercent = 0;
        let trend = 'up';
        
        if (prices.oil && prices.oil.price) {
          change = oilData.price - prices.oil.price;
          changePercent = (change / prices.oil.price) * 100;
          trend = change >= 0 ? 'up' : 'down';
        }
        
        newPrices.oil = {
          price: oilData.price,
          ask: oilData.price * 1.01,
          bid: oilData.price * 0.99,
          change: oilData.change || change,
          changePercent: oilData.changePercent || changePercent,
          trend: trend,
        };
        newHistory.oil = [...newHistory.oil.slice(-29), oilData.price];
      }
      
      // Process Diamond
      if (diamondData && diamondData.price) {
        let change = 0;
        let changePercent = 0;
        let trend = 'up';
        
        if (prices.diamond && prices.diamond.price) {
          change = diamondData.price - prices.diamond.price;
          changePercent = (change / prices.diamond.price) * 100;
          trend = change >= 0 ? 'up' : 'down';
        }
        
        newPrices.diamond = {
          price: diamondData.price,
          ask: diamondData.price * 1.05,
          bid: diamondData.price * 0.85,
          change: diamondData.change || change,
          changePercent: diamondData.changePercent || changePercent,
          trend: trend,
        };
        newHistory.diamond = [...newHistory.diamond.slice(-29), diamondData.price];
      }
      
      setPrices(newPrices);
      setPriceHistory(newHistory);
      setLastUpdated(timestamp);
      setLoading(false);
      
    } catch (err) {
      console.error('Failed to fetch prices:', err);
      setError('Unable to fetch live prices. Please check your internet connection.');
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  }, [fetchGoldPrice, fetchSilverPrice, fetchPlatinumPrice, fetchPalladiumPrice, fetchOilPrice, fetchDiamondPrice, prices, priceHistory]);

  const labels = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    return `${d.getMonth() + 1}/${d.getDate()}`;
  });

  useEffect(() => {
    fetchAllPrices();
    const interval = setInterval(fetchAllPrices, 60000);
    return () => clearInterval(interval);
  }, [fetchAllPrices]);

  const refreshPrices = () => {
    fetchAllPrices();
  };

  const kitcoLinks = [
    { name: 'Gold', symbol: 'XAU/USD', url: 'https://www.kitco.com/charts/gold', color: '#D4AF37', trend: prices.gold.trend || 'up' },
    { name: 'Silver', symbol: 'XAG/USD', url: 'https://www.kitco.com/charts/silver', color: '#C0C0C0', trend: prices.silver.trend || 'up' },
    { name: 'Platinum', symbol: 'XPT/USD', url: 'https://www.kitco.com/charts/platinum', color: '#E5E4E2', trend: prices.platinum.trend || 'down' },
    { name: 'Palladium', symbol: 'XPD/USD', url: 'https://www.kitco.com/charts/palladium', color: '#CED0D6', trend: prices.palladium.trend || 'up' },
  ];

  const marketInsights = [
    { title: 'Central Bank Buying', description: 'Global central banks added 1,037 tonnes of gold in 2023', value: '+1,037t', icon: <Analytics />, progress: 85 },
    { title: 'Industrial Demand', description: 'Silver demand from solar PV up 64% year-over-year', value: '+64%', icon: <BarChart />, progress: 75 },
    { title: 'Oil Demand Growth', description: 'Global oil demand expected to rise 2.2M bpd in 2024', value: '+2.2M', icon: <LocalFireDepartment />, progress: 70 },
    { title: 'Diamond Market', description: 'Rapaport prices stable with 30% below benchmark', value: '-30%', icon: <Diamond />, progress: 60 },
  ];

  const pricePredictions = [
    { metal: 'Gold', current: prices.gold.price ? `$${prices.gold.price.toFixed(2)}` : 'Loading...', prediction: '$3,200', analyst: 'Goldman Sachs', timeframe: '12 months' },
    { metal: 'Silver', current: prices.silver.price ? `$${prices.silver.price.toFixed(2)}` : 'Loading...', prediction: '$38.50', analyst: 'Bank of America', timeframe: '12 months' },
    { metal: 'Platinum', current: prices.platinum.price ? `$${prices.platinum.price.toFixed(2)}` : 'Loading...', prediction: '$1,150', analyst: 'Citi Group', timeframe: '12 months' },
    { metal: 'Diamond', current: prices.diamond.price ? `$${prices.diamond.price.toFixed(2)}` : 'Loading...', prediction: '$9,500', analyst: 'De Beers', timeframe: '12 months' },
  ];

  const allPricesData = [
    { name: 'Gold', symbol: 'XAU/USD', price: prices.gold.price, change: prices.gold.change, changePercent: prices.gold.changePercent, color: '#D4AF37', icon: <Whatshot />, key: 'gold' },
    { name: 'Silver', symbol: 'XAG/USD', price: prices.silver.price, change: prices.silver.change, changePercent: prices.silver.changePercent, color: '#C0C0C0', icon: <AttachMoney />, key: 'silver' },
    { name: 'Platinum', symbol: 'XPT/USD', price: prices.platinum.price, change: prices.platinum.change, changePercent: prices.platinum.changePercent, color: '#E5E4E2', icon: <Diamond />, key: 'platinum' },
    { name: 'Palladium', symbol: 'XPD/USD', price: prices.palladium.price, change: prices.palladium.change, changePercent: prices.palladium.changePercent, color: '#CED0D6', icon: <Diamond />, key: 'palladium' },
    { name: 'Rhodium', symbol: 'XRH/USD', price: prices.rhodium.price, change: prices.rhodium.change, changePercent: prices.rhodium.changePercent, color: '#D3D3D3', icon: <Diamond />, key: 'rhodium' },
    { name: 'Crude Oil', symbol: 'WTI/USD', price: prices.oil.price, change: prices.oil.change, changePercent: prices.oil.changePercent, color: '#2E7D32', icon: <OilBarrel />, key: 'oil' },
    { name: 'Diamond (1ct D VVS1)', symbol: 'RAP/DIA', price: prices.diamond.price, change: prices.diamond.change, changePercent: prices.diamond.changePercent, color: '#00BCD4', icon: <Diamond />, key: 'diamond' },
  ];

  if (loading && !prices.gold.price) {
    return (
      <Box sx={{ position: 'relative', minHeight: '100vh', overflowX: 'hidden' }}>
        <AbstractBackground />
        <Box sx={{ position: 'relative', zIndex: 1, py: 8 }}>
          <Container maxWidth="xl">
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3, mb: 4 }} />
            <Grid container spacing={3}>
              {[1,2,3,4,5,6,7].map(i => (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={i}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflowX: 'hidden' }}>
      <AbstractBackground />
      
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section with Market Data Background Image */}
        <Box
          sx={{
            minHeight: '45vh',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundImage: 'url("/images/marketdata.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.85) 100%)',
              zIndex: 0,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
              zIndex: 1,
            },
          }}
        >
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, py: { xs: 6, md: 8 } }}>
            <Stack spacing={2.5}>
              <Chip
                label="MARKET DATA"
                icon={<ShowChart sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.2),
                  color: '#D4AF37',
                  width: 'fit-content',
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  border: '1px solid rgba(212, 175, 55, 0.4)',
                  backdropFilter: 'blur(5px)',
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #FFD700 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                Precious Metals & Diamond Market Data
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#D4AF37',
                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                  fontWeight: 600,
                }}
              >
                Live Spot Prices & Rapaport Diamond Pricing
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha('#FFFFFF', 0.85),
                  maxWidth: 700,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                Live precious metals pricing from Metal Sentinel, Rapaport diamond prices, interactive charts, and institutional-grade market insights
              </Typography>
            </Stack>
          </Container>
        </Box>

        {/* Live Prices Grid */}
        <Container maxWidth="xl" sx={{ py: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 800 }}>
              Live Commodity & Diamond Prices
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Update />}
              onClick={refreshPrices}
              disabled={refreshing}
              sx={{
                borderColor: '#D4AF37',
                color: '#D4AF37',
                '&:hover': {
                  borderColor: '#FFD700',
                  bgcolor: alpha('#D4AF37', 0.1),
                },
              }}
            >
              {refreshing ? 'Updating...' : 'Refresh All'}
            </Button>
          </Box>
          
          {error && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: alpha('#f44336', 0.1), border: '1px solid #f44336', borderRadius: 2 }}>
              <Typography sx={{ color: '#f44336' }}>{error}</Typography>
            </Paper>
          )}
          
          <Grid container spacing={3}>
            {allPricesData.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={item.key}>
                <Box
                  sx={{
                    animation: `${fadeInUp} 0.6s ease-out ${index * 0.05}s forwards`,
                    opacity: 0,
                  }}
                >
                  <PriceCard
                    name={item.name}
                    symbol={item.symbol}
                    price={item.price}
                    change={item.change}
                    changePercent={item.changePercent}
                    color={item.color}
                    icon={item.icon}
                    lastUpdated={lastUpdated}
                    onRefresh={refreshPrices}
                    loading={loading && !item.price}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Diamond Price Section */}
        <Box sx={{ py: { xs: 6, md: 8 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="RAPAPORT DIAMOND PRICING"
                icon={<Diamond sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#00BCD4', 0.15),
                  color: '#00BCD4',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #00BCD4 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                Premium Diamond Pricing
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 700, mx: 'auto' }}
              >
                Based on Rapaport Diamond Price List • Certified & Conflict-Free
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  background: 'linear-gradient(90deg, #00BCD4, #26C6DA)',
                  margin: '0 auto',
                  mt: 3,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <DiamondPriceCalculator />
              </Grid>
              <Grid item xs={12} md={6}>
                <DiamondPriceTable />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Interactive Charts Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="INTERACTIVE CHARTS"
                icon={<ShowChart sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.15),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                Real-Time Price Charts
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 700, mx: 'auto' }}
              >
                30-day historical price trends for major commodities and diamonds
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 3,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <InteractiveChart 
                  title="Gold" 
                  color="#D4AF37" 
                  data={priceHistory.gold} 
                  labels={labels}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InteractiveChart 
                  title="Silver" 
                  color="#C0C0C0" 
                  data={priceHistory.silver} 
                  labels={labels}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InteractiveChart 
                  title="Platinum" 
                  color="#E5E4E2" 
                  data={priceHistory.platinum} 
                  labels={labels}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InteractiveChart 
                  title="Oil (WTI)" 
                  color="#2E7D32" 
                  data={priceHistory.oil} 
                  labels={labels}
                />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Kitco Live Charts Section - NEW ADDITION */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="KITCO LIVE CHARTS"
                icon={<ShowChart sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.15),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                Interactive Kitco Charts
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 700, mx: 'auto' }}
              >
                Live streaming charts powered by Kitco - The world's leading source for precious metals data
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 3,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <KitcoWidget type="gold" height={450} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={3}>
                  <KitcoWidget type="silver" height={200} showTitle={true} />
                  <KitcoWidget type="platinum" height={200} showTitle={true} />
                </Stack>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <KitcoWidget type="palladium" height={350} />
              </Grid>
              <Grid item xs={12} md={6}>
                <KitcoWidget type="copper" height={350} />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Kitco Market Data Table Section - NEW ADDITION */}
        <Box sx={{ py: { xs: 6, md: 8 }, position: 'relative', bgcolor: '#FFFFFF' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="COMPLETE MARKET DATA"
                icon={<Assessment sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.1),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 800,
                  color: '#0a0a2a',
                  mb: 2,
                }}
              >
                Full Precious Metals Dashboard
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha('#0a0a2a', 0.6), maxWidth: 700, mx: 'auto' }}
              >
                Complete market data including gold, silver, platinum, palladium, and more
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 3,
                  borderRadius: 2,
                }}
              />
            </Box>

            <KitcoMarketTable height={550} />
          </Container>
        </Box>

        {/* Market Insights Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="MARKET INSIGHTS"
                icon={<Assessment sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.1),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 800,
                  color: '#0a0a2a',
                  mb: 2,
                }}
              >
                Key Market Trends
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha('#0a0a2a', 0.6), maxWidth: 700, mx: 'auto' }}
              >
                Institutional insights shaping the commodities and diamond market
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 3,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={4}>
              {marketInsights.map((insight, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box
                    sx={{
                      animation: `${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                      opacity: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: '#F8F9FC',
                        border: `1px solid ${alpha('#D4AF37', 0.12)}`,
                        borderRadius: 3,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        height: '100%',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          borderColor: alpha('#D4AF37', 0.35),
                          boxShadow: `0 15px 30px ${alpha('#D4AF37', 0.12)}`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3.5, textAlign: 'center' }}>
                        <Box sx={{ color: '#D4AF37', fontSize: 44, mb: 2 }}>{insight.icon}</Box>
                        <Typography variant="h3" sx={{ color: '#D4AF37', fontWeight: 800, mb: 1, fontSize: { xs: 32, md: 36 } }}>
                          {insight.value}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: '#0a0a2a', fontWeight: 700, mb: 1 }}>
                          {insight.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.6), mb: 2 }}>
                          {insight.description}
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={insight.progress}
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            bgcolor: alpha('#D4AF37', 0.1),
                            '& .MuiLinearProgress-bar': {
                              bgcolor: '#D4AF37',
                              borderRadius: 2,
                            },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Price Predictions Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="ANALYST FORECASTS"
                icon={<Timeline sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.15),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                Price Predictions
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 700, mx: 'auto' }}
              >
                Institutional analyst forecasts and market outlook
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 3,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={4}>
              {pricePredictions.map((prediction, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box
                    sx={{
                      animation: `${scaleIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                      opacity: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: alpha('#FFFFFF', 0.05),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                        borderRadius: 3,
                        transition: 'all 0.4s ease',
                        textAlign: 'center',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          borderColor: alpha('#D4AF37', 0.4),
                          bgcolor: alpha('#FFFFFF', 0.08),
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        {prediction.metal === 'Diamond' ? 
                          <Diamond sx={{ color: '#00BCD4', fontSize: 32, mb: 1 }} /> :
                          <Diamond sx={{ color: '#D4AF37', fontSize: 32, mb: 1 }} />
                        }
                        <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 800, mb: 1 }}>
                          {prediction.metal}
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.5), mb: 2 }}>
                          Current: {prediction.current}
                        </Typography>
                        <Typography variant="h4" sx={{ color: prediction.metal === 'Diamond' ? '#00BCD4' : '#D4AF37', fontWeight: 800, mb: 1 }}>
                          {prediction.prediction}
                        </Typography>
                        <Divider sx={{ my: 1.5, borderColor: alpha('#D4AF37', 0.15) }} />
                        <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.6), display: 'block' }}>
                          {prediction.analyst}
                        </Typography>
                        <Chip 
                          label={prediction.timeframe}
                          size="small"
                          sx={{ 
                            mt: 1.5,
                            bgcolor: alpha('#D4AF37', 0.15), 
                            color: '#D4AF37',
                            fontSize: '0.65rem',
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Kitco Links Section (Original) */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="KITCO INTERACTIVE CHARTS"
                icon={<ShowChart sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.1),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: 28, md: 42 },
                  fontWeight: 800,
                  color: '#0a0a2a',
                  mb: 2,
                }}
              >
                Premium Interactive Charts
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: alpha('#0a0a2a', 0.6), maxWidth: 700, mx: 'auto' }}
              >
                Powered by Kitco - The leading source for precious metals data
              </Typography>
              <Box
                sx={{
                  width: 60,
                  height: 3,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 3,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={4}>
              {kitcoLinks.map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={item.name}>
                  <Box
                    sx={{
                      animation: `${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                      opacity: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: '#FFFFFF',
                        border: `1px solid ${alpha(item.color, 0.2)}`,
                        borderRadius: 3,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          borderColor: item.color,
                          boxShadow: `0 15px 30px ${alpha(item.color, 0.2)}`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Diamond sx={{ color: item.color, fontSize: 28 }} />
                            <Typography variant="h5" sx={{ color: '#0a0a2a', fontWeight: 800 }}>
                              {item.name}
                            </Typography>
                          </Box>
                          <Chip
                            label={item.trend === 'up' ? 'Bullish' : 'Bearish'}
                            size="small"
                            icon={item.trend === 'up' ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingDown sx={{ fontSize: 14 }} />}
                            sx={{
                              bgcolor: item.trend === 'up' ? alpha('#4caf50', 0.12) : alpha('#f44336', 0.12),
                              color: item.trend === 'up' ? '#4caf50' : '#f44336',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                        
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.5), mb: 2, fontFamily: 'monospace' }}>
                          {item.symbol}
                        </Typography>
                        
                        <Divider sx={{ my: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                        
                        <Button
                          variant="outlined"
                          endIcon={<OpenInNew />}
                          onClick={() => window.open(item.url, '_blank')}
                          fullWidth
                          sx={{
                            borderColor: item.color,
                            color: item.color,
                            fontWeight: 700,
                            py: 1,
                            '&:hover': {
                              borderColor: '#D4AF37',
                              bgcolor: alpha(item.color, 0.05),
                            },
                          }}
                        >
                          View Interactive Chart
                        </Button>
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Resources Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInLeft} 0.6s ease-out forwards`, opacity: 0 }}>
                  <Chip
                    label="RESOURCES"
                    icon={<Public sx={{ fontSize: 14 }} />}
                    sx={{
                      bgcolor: alpha('#D4AF37', 0.15),
                      color: '#D4AF37',
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, md: 36 },
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 2,
                    }}
                  >
                    Stay Informed with Real-Time Data
                  </Typography>
                  <Typography variant="body1" sx={{ color: alpha('#FFFFFF', 0.8), mb: 3, lineHeight: 1.7 }}>
                    Access comprehensive market data from Metal Sentinel API, including live spot prices, Rapaport diamond prices, historical charts, 
                    and detailed analysis from leading industry sources.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
                    {['Live Spot Prices', 'Rapaport Diamond Prices', 'Historical Data', 'Technical Analysis', 'Market News'].map((item) => (
                      <Chip 
                        key={item}
                        label={item}
                        size="small"
                        icon={<Verified sx={{ fontSize: 12 }} />}
                        sx={{ 
                          bgcolor: alpha('#D4AF37', 0.1), 
                          color: '#D4AF37',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          '&:hover': {
                            bgcolor: alpha('#D4AF37', 0.2),
                          },
                        }} 
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInRight} 0.6s ease-out 0.2s forwards`, opacity: 0 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      bgcolor: alpha('#FFFFFF', 0.05),
                      backdropFilter: 'blur(10px)',
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 3,
                    }}
                  >
                    <ShowChart sx={{ fontSize: 56, color: '#D4AF37', mb: 2, animation: `${pulse} 2s ease-in-out infinite` }} />
                    <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 800, mb: 2 }}>
                      Why Monitor Commodities & Diamonds?
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.7), mb: 3, lineHeight: 1.7 }}>
                      Precious metals and diamonds serve as a hedge against inflation, a safe-haven asset during market volatility, 
                      and a strategic portfolio diversifier for institutional investors.
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: alpha('#D4AF37', 0.15) }} />
                    <Stack direction="row" spacing={4} justifyContent="center" flexWrap="wrap">
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#D4AF37', fontWeight: 800 }}>+15%</Typography>
                        <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5) }}>Gold Return (YTD)</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#D4AF37', fontWeight: 800 }}>+22%</Typography>
                        <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5) }}>Silver Return (YTD)</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#00BCD4', fontWeight: 800 }}>+5%</Typography>
                        <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5) }}>Diamond (YTD)</Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ py: 8 }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Card
              elevation={0}
              sx={{
                p: 5,
                bgcolor: '#FFFFFF',
                border: `1px solid ${alpha('#D4AF37', 0.2)}`,
                borderRadius: 3,
              }}
            >
              <Diamond sx={{ fontSize: 56, color: '#D4AF37', mb: 2, animation: `${pulse} 2s ease-in-out infinite` }} />
              <Typography variant="h4" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 2 }}>
                Ready to Invest?
              </Typography>
              <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.6), mb: 3 }}>
                Contact our trading desk for competitive pricing on diamonds and precious metals
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                  color: '#0a0a2a',
                  fontWeight: 700,
                  px: 5,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 20px ${alpha('#D4AF37', 0.3)}`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Contact Trading Desk
              </Button>
            </Card>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default MarketData;